import React, { useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import {
  BrowserRouter as Router,
  Redirect,
  Switch,
  Route,
  useParams,
} from 'react-router-dom';
import {
  theme,
  ThemeProvider as LibThemeProvider,
} from '@m12s/component-library';
import { I18nextProvider } from 'react-i18next';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';

import './App.css';
import i18n from 'lib/translations/i18n';
import Routes from 'lib/utils/routes';
import { ApolloProvider } from '@apollo/client';
import { client } from 'lib/api';
import { useDocumentTitle, useAnalytics, useConnectionStatus } from 'lib/hooks';

import { UpdatePopup } from 'components/UpdatePopup';
import CategorizePage from 'pages/Categorize';
import MachinePage from 'pages/Machine';
import SelectJobPage from 'pages/SelectJob';
import RejectPartsPage from 'pages/RejectParts';
import SwitchActivities from 'pages/SwitchActivities';
import SwitchMachinesPage from 'pages/SwitchMachines';
import OAuthLogin from 'pages/OAuthLogin';
import Pwa from 'pages/Pwa';
import Authorize from 'pages/Authorize';
import WorkflowTriggerPage from 'pages/WorkflowTrigger';
import StartActivityNewOperation from 'pages/StartActivityNewOperation';
import JobInfo from 'pages/SelectJob/JobInfo';

import { TickerProvider } from 'components/TickerProvider';
import LaunchDarklyProvider from 'components/LaunchDarklyProvider';
import { PendingStates } from 'components/PendingStates';
import { UnauthenticatedRoute } from 'components/route/UnauthenticatedRoute';
import { PrivateRoute } from 'components/route/PrivateRoute';
import { BarcodeScannerProvider } from 'lib/contexts/BarcodeScannerContext';
import { ScrimProvider } from 'lib/contexts/ScrimContext';

import { useSelector } from 'react-redux';
import { Reconnect } from 'components/Reconnect';
import { HFLockout } from 'components/HFLockout';
import { DataProvider } from 'components/DataProvider/DataProvider';
import { AccountProvider } from 'components/AccountProvider';
import { useTimesync } from 'lib/hooks/useTimesync';
import { useStatusPage } from 'lib/hooks/useStatusPage';
import { useLocale } from 'lib/hooks/useLocale';

import NoSleep from 'nosleep.js';

import JobInfoERP from 'pages/SelectJob/JobInfoERP/index';
import { CustomToastContainer } from 'lib/utils/toast';
import { getWorkOrderFlowEnabled } from 'lib/selectors/getWorkOrderFlowEnabled';
import { SelectActivity } from 'components/SelectActivity';
import { getSelectActivityOptions } from 'lib/selectors/getSelectActivity';
import { JobStartAtDialog } from 'components/JobStartAtDialog';
import { JobCloseAtDialog } from 'components/JobCloseAtDialog';
import { PartCountMultiplierDialog } from 'components/PartCountMultiplierDialog';
import { SelectOperator } from 'components/SelectOperator';
import { ClockOutModal } from 'components/LaborTicket/ClockOutModal';
import { ClockOutEmbedWrapper } from 'components/LaborTicket/ClockOutEmbedWrapper';
import { UnattendedOperatorPrompt } from 'components/LaborTicket/UnattendedOperatorPrompt';
import { SetupActionModal } from 'components/LaborTicket/SetupActionModal';
import { ShiftHandoverWrapper } from 'components/LaborTicket/ShiftHandoverWrapper';
import { ShiftIntroWrapper } from 'components/LaborTicket/ShiftIntroWrapper';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { getCompanySettings } from 'lib/selectors/getCompanySettings';
import { getLaunchDarklyFlags } from 'lib/selectors/getLaunchDarklyFlags';
import { isJobDocsFeatureEnabled } from 'lib/jobDocsGate';
import { JobDocsConfigProvider } from 'hooks/useJobDocsConfig';
import { JOB_DOCS_CLIENT_URL, JOB_DOCS_ENABLED } from 'lib/appConfig';
import LaborTicketWrapper from 'components/LaborTicketWrapper';
import { MMEmbeddableZoneProvider } from '@machinemetrics/mm-react-embeddable';
import { EmbeddableAuthorizationModal } from 'components/Embeddable';
import { brokerEmbeddedCredential } from 'lib/api/embeddedCredential';
import session from 'lib/api/links/session';

const RedirectToMachineHomePage = () => {
  const { id } = useParams();
  return <Redirect to={Routes.machineIdHomePath(id)} />;
};

// Create a custom Chakra theme that preserves your existing fonts
const customChakraTheme = extendTheme({
  fonts: {
    heading: "'Noto Sans', sans-serif",
    body: "'Noto Sans', sans-serif",
  },
  styles: {
    global: {
      // Prevent Chakra from overriding your existing styles
      body: {
        fontFamily: "'Noto Sans', sans-serif",
      },
    },
  },
});

const App = () => {
  const noSleep = new NoSleep();

  const workOrdeFlowEnabled = useSelector(getWorkOrderFlowEnabled);
  const selectActivityOptions = useSelector(getSelectActivityOptions);
  const selectActivityKey =
    selectActivityOptions?.workOrderOp?.workOrderOperationRef ?? 'no-woo';
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);
  const companySettings = useSelector(getCompanySettings);
  const launchDarklyFlags = useSelector(getLaunchDarklyFlags);

  // Job Docs is gated by the JobDocsDelivery LD flag (authoritative and
  // default-OFF — must resolve to literal `true`) AND the JOB_DOCS_ENABLED env
  // default (per-host wiring) AND the per-company MES settings — ANDed, so any
  // explicit `false` disables. Undefined company settings stay enabled.
  const jobDocsFeatureEnabled = isJobDocsFeatureEnabled({
    ldFlags: launchDarklyFlags,
    envEnabled: JOB_DOCS_ENABLED,
    companySettings,
  });

  useEffect(() => {
    document.addEventListener(
      'click',
      function enableNoSleep() {
        document.removeEventListener('click', enableNoSleep, false);
        noSleep.enable();
      },
      false
    );

    return noSleep.disable();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useDocumentTitle();
  useAnalytics();
  useConnectionStatus();
  useTimesync();
  useStatusPage();
  useLocale();
  return (
    <ThemeProvider theme={theme}>
      <LibThemeProvider theme={theme}>
        <I18nextProvider i18n={i18n}>
          <ChakraProvider theme={customChakraTheme} resetCSS={false}>
            <BarcodeScannerProvider>
              {/* Stacked-modal scrim accounting: dimming surfaces (drawer-look
                  pages, MMDrawer overlays) register here so only the
                  bottom-most layer of a stack draws a scrim. */}
              <ScrimProvider>
                {/* Config-only context (no UI/effects); the embed zone is
                  mounted lazily by the JobDocsFooter's drawer on the Job Info
                  screens. */}
                <JobDocsConfigProvider
                  enabled={jobDocsFeatureEnabled}
                  clientUrl={JOB_DOCS_CLIENT_URL}
                >
                  <TickerProvider>
                    <CustomToastContainer closeButton={false} hideProgressBar />
                    <SelectOperator />
                    {/* key forces remount on WOO change so SelectActivity's local PCM useState resets to the new job's default */}
                    <SelectActivity key={selectActivityKey} />
                    <JobStartAtDialog />
                    <JobCloseAtDialog />
                    <PartCountMultiplierDialog />
                    <UpdatePopup />
                    <Reconnect />
                    <Switch>
                      <PrivateRoute exact path={Routes.rootPath()}>
                        <Redirect to={Routes.switchMachinesPath()} />
                      </PrivateRoute>
                      <UnauthenticatedRoute exact path={Routes.loginPath()}>
                        <OAuthLogin />
                      </UnauthenticatedRoute>
                      <UnauthenticatedRoute exact path={Routes.authorizePath()}>
                        <Authorize />
                      </UnauthenticatedRoute>
                      <Route exact path={Routes.pwaPath()}>
                        <Pwa />
                      </Route>
                      <PrivateRoute path="/">
                        <AccountProvider>
                          <Route exact path={Routes.switchMachinesPath()}>
                            <SwitchMachinesPage />
                          </Route>
                          <Route path={Routes.machineIdPath.pattern}>
                            <DataProvider>
                              <MMEmbeddableZoneProvider
                                credentialBroker={brokerEmbeddedCredential}
                                ready={Boolean(session.getApiKey())}
                              >
                                <EmbeddableAuthorizationModal />
                                <HFLockout />
                                <LaborTicketWrapper />
                                {clockOutFlowEnabled && (
                                  <>
                                    <ClockOutModal />
                                    <ClockOutEmbedWrapper />
                                  </>
                                )}
                                <UnattendedOperatorPrompt />
                                <SetupActionModal />
                                {shiftHandoverEnabled && (
                                  <ShiftHandoverWrapper />
                                )}
                                {shiftHandoverEnabled && <ShiftIntroWrapper />}
                                <PendingStates>
                                  <Switch>
                                    <Route
                                      exact
                                      path={Routes.machineIdPath.pattern}
                                      component={RedirectToMachineHomePage}
                                    />
                                    <Route
                                      exact
                                      path={
                                        Routes.machineIdRejectPartsPath.pattern
                                      }
                                      component={RejectPartsPage}
                                    />
                                    {/* Prefix match: also covers the tab-nested
                                      ERP current-job overlay path, keeping the
                                      machine page mounted beneath it. */}
                                    <Route
                                      path={Routes.machineIdHomePath.pattern}
                                      component={MachinePage}
                                    />
                                    <Route
                                      path={
                                        Routes.machineIdCategorizePath.pattern
                                      }
                                      component={CategorizePage}
                                    />
                                    <Route
                                      exact
                                      path={
                                        Routes.machineIdSelectJobPath.pattern
                                      }
                                      component={SelectJobPage}
                                    />
                                    {/* Legacy (non-ERP) JobInfo. The ERP flow
                                      instead uses the tab-nested overlay path
                                      (rendered after this Switch); a stale
                                      un-nested URL under the ERP flow falls
                                      back to the machine home. */}
                                    <Route
                                      exact
                                      path={
                                        Routes.machineIdCurrentOperationInfoPath
                                          .pattern
                                      }
                                      render={({ match, location }) => {
                                        return workOrdeFlowEnabled ? (
                                          <Redirect
                                            to={Routes.machineIdHomePath(
                                              match.params.id
                                            )}
                                          />
                                        ) : (
                                          /* location carries MachineHeader's router state (job, isStartingNewRun) */
                                          <JobInfo location={location} />
                                        );
                                      }}
                                    />
                                    <Route
                                      path={
                                        Routes
                                          .machineIdStartActivityNewOperationPath
                                          .pattern
                                      }
                                      component={StartActivityNewOperation}
                                    />
                                    <Route
                                      path={
                                        Routes.machineIdSwitchActivitiesPath
                                          .pattern
                                      }
                                      component={SwitchActivities}
                                    />
                                    <Route
                                      path={
                                        Routes.machineIdSelectWorkflowPath
                                          .pattern
                                      }
                                      component={WorkflowTriggerPage}
                                    />
                                  </Switch>
                                  {/* Overlay route (ERP flow): the drawer-look
                                    current-job page renders ON TOP of the
                                    machine page. The path stays nested under
                                    home/:tab, so MachinePage (whose UI lives
                                    inside its own home/:tab route) keeps its
                                    full subtree — header included — mounted
                                    beneath the fixed backdrop. */}
                                  {workOrdeFlowEnabled && (
                                    <Route
                                      exact
                                      path={
                                        Routes
                                          .machineIdHomeTabCurrentOperationInfoPath
                                          .pattern
                                      }
                                      component={JobInfoERP}
                                    />
                                  )}
                                </PendingStates>
                              </MMEmbeddableZoneProvider>
                            </DataProvider>
                          </Route>
                        </AccountProvider>
                      </PrivateRoute>
                    </Switch>
                  </TickerProvider>
                </JobDocsConfigProvider>
              </ScrimProvider>
            </BarcodeScannerProvider>
          </ChakraProvider>
        </I18nextProvider>
      </LibThemeProvider>
    </ThemeProvider>
  );
};

const HostMiddleware = () => {
  return (
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  );
};

export default () => {
  return (
    <Router>
      <LaunchDarklyProvider>
        <HostMiddleware />
      </LaunchDarklyProvider>
    </Router>
  );
};
